import React, { useEffect } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { useAtomValue } from 'jotai';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { BaseChart, Retry } from '@components/common';
import DragHandle from '@components/common/DnD/DragHandle';
import useToolbox from '@components/metrics-panel-hz/hooks/useToolbox';
import { filterNAMetricsAtom } from '@src/atoms/page';
import { getFirstAppliedFilter } from '@store/common/selectors';
import { globalOrderBySelector } from '@store/trends/base/selectors';
import Header from './Header';
import RemoveMetric from './RemoveMetric';
import { sizeAtom } from '../atoms';
import { CHART_SIZE_MAPPING } from '../control-panel/Resize';
import SecondaryMetric from './SecondaryMetric';
import cardStyles from './index.less';

// converted from class componenet
function MetricCard(props) {
  const { metric, isSelected, activeTool, onSelect, diagMode } = props;
  const appliedFilter = useSelector(getFirstAppliedFilter);
  const metricsToNA = useAtomValue(filterNAMetricsAtom);
  const size = useAtomValue(sizeAtom);
  const secondaryMetric = useSelector(globalOrderBySelector);

  const classes = classNames(cardStyles.metricCard, {
    active: isSelected,
  });

  const naMode = metricsToNA.includes(metric);

  const {
    chartMode,
    setChartMode,
    chartInstanceRefCb,
    distributionQueryState,
    instance,
    toggleLegend,
    distribution,
    selectDistribution,
  } = useToolbox({ metric, activeTool, appliedFilter, naMode, diagMode, isSelected });

  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: metric });

  useEffect(() => {
    if (isSelected && instance) {
      instance.resize();
    }
  }, [diagMode, isSelected, instance]);

  const showSecondaryMetric = diagMode && isSelected && secondaryMetric !== metric;

  const dndStyle: any = {
    transform: CSS.Transform.toString(transform),
    transition,
    // primary chart height for diag is same as large mode, secondary adds additional height
    height: diagMode
      ? showSecondaryMetric
        ? CHART_SIZE_MAPPING.diag.height
        : CHART_SIZE_MAPPING.l.height
      : CHART_SIZE_MAPPING[size].height,
  };

  // hide all cards except selected card for diagnostics mode
  if (diagMode && !isSelected) {
    dndStyle.display = 'none';
  }

  const onClick = () => {
    onSelect(metric);
  };

  const chartClass = classNames(cardStyles.bodySection, {
    [cardStyles.naMode]: naMode,
  });

  return (
    <div key={metric} id={metric} className={classes} ref={setNodeRef} style={dndStyle}>
      <DragHandle {...attributes} {...listeners} />
      <RemoveMetric metric={metric} />
      <div
        id={`trends-${metric}`}
        className={chartClass}
        tabIndex={-1}
        role="button"
        onClick={onClick}
      >
        <Header
          {...props}
          chartMode={chartMode}
          setChartMode={setChartMode}
          toggleLegend={toggleLegend}
          distribution={distribution}
          selectDistribution={selectDistribution}
          naMode={naMode}
        />
        <Retry {...distributionQueryState}>
          <BaseChart
            key={metric}
            chartId={metric}
            chartInstanceRefCb={chartInstanceRefCb}
            style={{ height: '100%' }}
          />
        </Retry>
        {showSecondaryMetric && <SecondaryMetric activeTool={activeTool} />}
      </div>
    </div>
  );
}

export default React.memo(MetricCard);
